// This mixin adds a scale property, with getters and setters
// for changing it with an encapsulated private property:
function ApplyEmitter(Base) {
    return class Emitter extends Base {
        constructor() {
            super(...arguments);
            // Map of string containing a list of callback
            this._emitterEvents = new Map();
        }
        on(eventName, callback) {
            if (!this._emitterEvents.has(eventName)) {
                this._emitterEvents.set(eventName, new Map());
            }
            this._emitterEvents
                .get(eventName)
                .set(Symbol(), callback);
            return this;
        }
        /**
         * Trigger the callbacks registered for a given event
         */
        emit(eventName, ...datas) {
            var eventList = eventName instanceof Array ? eventName : [eventName];
            eventList.forEach(name => {
                if (this._emitterEvents.has(name)) {
                    this._emitterEvents.get(name).forEach(callback => {
                        callback.call(this, ...datas);
                    });
                }
            });
            return this;
        }
        /**
         * Register a callback for an event that will be triggered once.
         */
        once(eventName, callback) {
            var onceCallback = (e) => {
                callback.call(this, e);
                this.off(eventName, onceCallback);
            };
            this.on(eventName, onceCallback);
            return this;
        }
        /**
         * Register a callback for an event that will be triggered once and resolve through a Promise
         */
        oncePromise(eventName) {
            return new Promise((resolve) => {
                this.once(eventName, (event) => {
                    resolve(event);
                });
            });
        }
        /**
         * Unregister a callback from an event
         */
        off(eventName, callback) {
            if (!this._emitterEvents.has(eventName))
                return null;
            this._emitterEvents
                .get(eventName)
                .forEach((c, i) => {
                if (c === callback) {
                    this._emitterEvents.get(eventName).delete(i);
                }
            });
            return this;
        }
        /**
         * Alias for `on`
         */
        addEventListener(eventName, callback) {
            return this.on(eventName, callback);
        }
        /**
         * Alias for `off`
         */
        removeEventListener(eventName, callback) {
            return this.off(eventName, callback);
        }
    };
}
class Emitter {
}
var Emitter$1 = ApplyEmitter(Emitter);

var LoadingStatus;
(function (LoadingStatus) {
    LoadingStatus[LoadingStatus["IDLE"] = 1] = "IDLE";
    LoadingStatus[LoadingStatus["LOADED"] = 2] = "LOADED";
    LoadingStatus[LoadingStatus["PENDING"] = 3] = "PENDING";
    LoadingStatus[LoadingStatus["ERROR"] = 4] = "ERROR";
})(LoadingStatus || (LoadingStatus = {}));
function getIncrementalId() {
    let counter = 1;
    return (() => counter++)();
}
class File extends Emitter$1 {
    constructor(args) {
        super();
        this.status = LoadingStatus.IDLE;
        this.computedPath = null;
        this.parent = null;
        this.size = 0;
        this.loaded = 0;
        if (typeof args === 'string')
            return File.fromString(args);
        if (!args.path)
            throw new Error('Loader.File: Argument "path" cannot be falsy');
        if (typeof args.name !== 'string')
            throw new Error('Loader.File: Argument "name" cannot be falsy');
        this.id = getIncrementalId();
        this.status = LoadingStatus.IDLE;
        this.path = args.path;
        this.name = args.name;
        this.size = args.size ? args.size : 0;
        this.on('progress', (event) => {
            this.loaded = event.loaded;
            if (this.size === 0) {
                this.size = event.total;
            }
        });
    }
    static fromString(def) {
        const f = new File({
            name: '',
            path: def
        });
        f.name = 'file-' + f.id;
        return f;
    }
    get isLoaded() {
        return this.status === LoadingStatus.LOADED;
    }
    load() {
        return this.parent.context.load(this);
    }
    handleEventsIfLoaded(bubling = true) {
        if (this.isLoaded) {
            this.emit('load', this);
        }
        if (bubling && this.parent) {
            this.parent.handleEventsIfLoaded(bubling);
        }
    }
    computePath() {
        let parent = this.parent;
        let baseUrl = '';
        while (parent) {
            baseUrl += parent.baseUrl;
            parent = parent.parent || null;
        }
        this.computedPath = baseUrl + this.path;
        return this.computedPath;
    }
}

function getIncrementalId$1() {
    let counter = 1;
    return (() => counter++)();
}
class Group extends Emitter$1 {
    constructor(args = {}) {
        super();
        this.baseUrl = '';
        this._files = [];
        this.groups = [];
        this.parent = null;
        this._isLoaded = true;
        if (args instanceof Array)
            return Group.fromFileArray(args);
        if (typeof args.name !== "string")
            throw new Error('Loader.File: Argument "name" cannot be falsy');
        this.id = getIncrementalId$1();
        this.name = args.name;
        this.baseUrl = args.baseUrl ? args.baseUrl.toString() : '';
        (args.files || []).forEach(p => this.addFile(p));
        (args.groups || []).forEach(p => this.addGroup(p));
    }
    get(name) {
        let match = null;
        if (this.name === name)
            match = this;
        this.groups.forEach(g => {
            const t = g.get(name);
            if (t) {
                match = t;
            }
        });
        const file = this._files.find(f => f.name === name);
        if (file) {
            match = file;
        }
        return match;
    }
    addFiles(files) {
        files.forEach((a) => {
            this.addFile(a);
        });
    }
    addFile(def) {
        const file = new File(def);
        file.once('load', () => {
            this.emit('file:load', file);
        });
        file.on('progress', (progress) => {
            const { loaded, total } = this.files.reduce((acc, value) => {
                acc.loaded += value.loaded;
                acc.total += value.size;
                return acc;
            }, { loaded: 0, total: 0 });
            this.emit('progress', new ProgressEvent('group', { loaded, total }));
        });
        file.parent = this;
        this._files.push(file);
        this._isLoaded = false;
        return file;
    }
    addGroup(def) {
        const group = new Group(def);
        group.on('file:load', (result) => this.emit('file:load', result));
        group.parent = this;
        this.groups.push(group);
        this._isLoaded = false;
        return group;
    }
    setContext(context) {
        this.context = context;
        this.groups.forEach((g) => {
            g.setContext(context);
        });
    }
    load() {
        return this.context.load(this);
    }
    /**
     * Return all the files contain in a folder by status
     */
    getFilesByStatus(status) {
        return this._files
            .filter(file => file.status === status)
            .concat(this.groups.reduce((acc, value) => acc.concat(value.getFilesByStatus(status)), []));
    }
    get files() {
        return this._files
            .concat(this.groups.reduce((acc, value) => acc.concat(value.files), []));
    }
    get idleFiles() {
        return this.getFilesByStatus(LoadingStatus.IDLE);
    }
    get pendingFiles() {
        return this.getFilesByStatus(LoadingStatus.PENDING);
    }
    get loadedFiles() {
        return this.getFilesByStatus(LoadingStatus.LOADED);
    }
    get isLoaded() {
        if (this._isLoaded)
            return this._isLoaded;
        const isFilesLoaded = !this._files.find(file => file.status === LoadingStatus.PENDING || file.status === LoadingStatus.IDLE);
        const isGroupsLoaded = !this.groups.find(group => !group.isLoaded);
        this._isLoaded = isFilesLoaded && isGroupsLoaded;
        return this._isLoaded;
    }
    handleEventsIfLoaded(bubling = true) {
        if (this.isLoaded) {
            this.emit('load', this);
        }
        if (bubling && this.parent) {
            this.parent.handleEventsIfLoaded(bubling);
        }
    }
    static fromFileArray(files) {
        const g = new Group({
            name: ``,
            files
        });
        g.name = `group-${(g.id).toString()}`;
        return g;
    }
}

class Manager extends Emitter$1 {
    constructor(rules, { baseUrl = '' } = {}) {
        super();
        this.groups = new Map();
        this.files = new Map();
        this.rules = rules;
        this.initDefaultGroup(baseUrl);
    }
    get defaultGroup() {
        return this.groups.get('default');
    }
    initDefaultGroup(baseUrl) {
        const group = new Group({ name: 'default', baseUrl });
        group.on('load', () => {
            this.emit('load', group);
        });
        group.on('progress', (data) => {
            this.emit('progress', data);
        });
        this.groups.set('default', group);
    }
    get(name) {
        return this.defaultGroup.get(name);
    }
    loadGroup(GroupOptions) {
        const group = this.addGroup(GroupOptions);
        this.load(group);
        return group;
    }
    loadFile(def) {
        const file = this.addFile(def);
        return this.load(file);
    }
    addFile(def) {
        const file = this.groups.get('default').addFile(def);
        return file;
    }
    addFiles(files) {
        files.forEach(file => {
            this.addFile(file);
        });
    }
    addGroup(args) {
        return this.defaultGroup.addGroup(args);
    }
    load(args = [...this.groups.values()]) {
        let files = [];
        if (args instanceof Group)
            files = args.idleFiles;
        if (args instanceof File)
            files = [args];
        if (args instanceof Array && args[0] instanceof File)
            files = args;
        if (args instanceof Array && args[0] instanceof Group)
            files = args.reduce((acc, group) => {
                return acc.concat(group.idleFiles);
            }, []);
        const promiseFiles = files.map(file => new Promise((resolve, reject) => {
            const rule = this.rules.find((rule) => {
                if (file.path.match(rule.test)) {
                    return true;
                }
            });
            if (!rule) {
                console.error(`Loader: No loader for file "${file.path}"`);
                return;
            }
            const loader = rule.loader;
            file.status = LoadingStatus.PENDING;
            file.computePath();
            if (this.files.get(file.computedPath)) {
                resolve(file.subject);
                return;
            }
            loader.load(file).then(object => {
                this.files.set(file.computedPath, file);
                file.subject = object;
                file.status = LoadingStatus.LOADED;
                file.handleEventsIfLoaded();
                resolve(file.subject);
            }).catch((error) => {
                console.error(error);
            });
        }));
        if (promiseFiles.length === 1) {
            return promiseFiles[0];
        }
        return Promise.all(promiseFiles);
    }
}

class Loader extends Emitter$1 {
    load(_) {
        console.error('Loader need to implement the load function');
        return new Promise((resolve, reject) => {
            reject();
        });
    }
}

class ThreeLoader extends Loader {
    constructor(loader) {
        super();
        this._loader = loader;
    }
    load(file) {
        return new Promise((resolve, reject) => {
            this._loader.load(file.computedPath, 
            // onLoad
            (event, test) => {
                file.emit('load', event);
                resolve(event);
            }, 
            // onProgress
            (progress) => {
                file.emit('progress', progress);
            }, 
            // onError
            (error) => {
                file.emit('error', error);
                reject(error);
            });
        });
    }
}

export default Manager;
export { File, Group, Loader, Manager, ThreeLoader };
